import { useAuth0 } from "@auth0/auth0-react";
import { PaperAirplaneIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { unwrapConnection } from "@utils/utils";
import { format } from "date-fns";
import React, { useEffect, useRef, useState } from "react";

import { Paragraph, TextArea } from "@fronterahealth/frontera-ui-components";

import {
  ChatType,
  useCreateChatNoteMutation,
  useDeleteChatNoteMutation,
  useGetChatNotesQuery,
} from "@api/graphql/types-and-hooks";
import { ConfirmationDialog } from "@components/ConfirmationDialog/ConfirmationDialog";
import { InlineEditField } from "@components/ProgrammingReview/InlineEditField";
import { UserInitialsView } from "@components/UserInitial/UserInitialsView";
import { notifyError } from "@components/notifications/notifications";

interface ExpertChatNotesProps {
  chat: ChatType;
}

export const ExpertChatNotes: React.FC<ExpertChatNotesProps> = ({ chat }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useGetChatNotesQuery({ chatId: chat.id }, { queryKey: ["getChatNotes", chat.id] });

  const { user } = useAuth0();
  const [newNote, setNewNote] = useState<string>("");

  const [openDeleteNote, setOpenDeleteNote] = useState<boolean>(false);
  const [deleteNoteId, setDeleteNoteId] = useState<string>("");

  const createChatNoteMutation = useCreateChatNoteMutation({});
  const deleteChatNoteMutation = useDeleteChatNoteMutation({});

  const notes = unwrapConnection(data?.chatNotes) || [];

  // increase text area for new lines
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [newNote]);

  const handleSaveNote = async (
    noteId: string,
    fields: Partial<{ note: string }>,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!fields.note?.trim()) {
      return { success: false, error: "Note content cannot be empty" };
    }

    try {
      const response = await createChatNoteMutation.mutateAsync({
        input: {
          chatNoteId: noteId,
          chatId: chat.id,
          content: fields.note.trim(),
        },
      });

      if (!response.createChatNote?.success) {
        return {
          success: false,
          error: response.createChatNote?.message || "Failed to update note",
        };
      }

      queryClient.invalidateQueries({ queryKey: ["getChatNotes", chat.id] });

      return { success: true };
    } catch (_error) {
      return { success: false, error: "Failed to update note" };
    }
  };

  const handleDelete = async () => {
    if (!deleteNoteId) return;

    try {
      const response = await deleteChatNoteMutation.mutateAsync({
        chatNoteId: deleteNoteId,
      });

      if (!response.deleteChatNote?.success) {
        notifyError(response.deleteChatNote?.message || "Failed to delete note");
        return;
      }

      setDeleteNoteId("");

      queryClient.invalidateQueries({ queryKey: ["getChatNotes", chat.id] });
    } catch (_error) {
      notifyError("Failed to delete note");
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      const response = await createChatNoteMutation.mutateAsync({
        input: {
          chatNoteId: null,
          chatId: chat.id,
          content: newNote.trim(),
        },
      });

      if (!response.createChatNote?.success) {
        notifyError(response.createChatNote?.message || "Failed to add note");
        return;
      }

      setNewNote("");

      queryClient.invalidateQueries({ queryKey: ["getChatNotes", chat.id] });
    } catch (_error) {
      notifyError("Failed to add note.");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleAddNote();
    }
  };

  return (
    <div className="flex flex-col gap-4 overflow-y-auto no-scrollbar">
      <div>
        {(notes.length === 0 || isLoading) && (
          <div className="flex flex-row justify-center px-4 py-2">
            <Paragraph size="small" colorType="secondary">
              No notes yet
            </Paragraph>
          </div>
        )}

        {notes.map((note) => {
          const userFirstName = note.user.provider?.providermetadata?.firstName;
          const userLastName = note.user.provider?.providermetadata?.lastName;
          const isOwner = note?.user?.auth0id === user?.sub;

          return (
            <div key={note.id} className="flex flex-row gap-4 px-4 group overflow-hidden">
              <UserInitialsView
                className="h-8! w-8! min-h-8! min-w-8! text-[16px]! leading-4"
                firstName={userFirstName || ""}
                lastName={userLastName || ""}
              />
              <div className="flex flex-col gap-1 flex-1 min-w-0">
                <div className="flex flex-row gap-2">
                  <Paragraph displayType="loud" className="text-[#0E2528]">
                    {userFirstName} {userLastName}
                  </Paragraph>
                  <Paragraph className="text-[#706B61] leading-[16px]">
                    {format(new Date(note?.createdDate), "M/d/yyyy")}
                  </Paragraph>
                </div>

                {isOwner ? (
                  <div className="flex flex-row gap-2 items-center w-full min-w-0">
                    <InlineEditField
                      value={note?.content || ""}
                      isMultiline={true}
                      fieldKey="note"
                      onSave={(fields) => handleSaveNote(note?.id || "", fields)}
                      title=""
                    />
                    <XMarkIcon
                      className="min-w-5! min-h-5! max-h-5! max-w-5! opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer text-text-primary"
                      onClick={() => {
                        setDeleteNoteId(note.id);
                        setOpenDeleteNote(true);
                      }}
                    />
                  </div>
                ) : (
                  <div className="pb-4">
                    <Paragraph colorType="primary">{note.content}</Paragraph>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-row items-center rounded-xl border bg-white pr-4 mx-4">
        <div className="w-full min-w-0">
          <TextArea
            id="new-note"
            ref={textareaRef}
            label=""
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add a note..."
            rows={1}
            className="border-0 outline-hidden text-text-secondary focus:ring-0 text-sm w-full resize-none overflow-hidden"
            onKeyDown={handleKeyDown}
          />
        </div>
        <PaperAirplaneIcon
          className="h-5 w-5 text-text-secondary cursor-pointer"
          onClick={newNote.trim() ? handleAddNote : undefined}
        />
      </div>

      <ConfirmationDialog
        open={openDeleteNote}
        onCancelClick={() => {
          setDeleteNoteId("");
          setOpenDeleteNote(false);
        }}
        onSubmitClick={() => {
          setOpenDeleteNote(false);
          if (deleteNoteId) {
            handleDelete();
          }
        }}
        title="Delete Note"
        content="Are you sure you want to delete this note? This action cannot be undone."
        submitButtonText="Yes, Delete"
      />
    </div>
  );
};
